import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { uniq } from 'lodash';
import { toastr } from 'react-redux-toastr';
import { push, replace } from 'react-router-redux';
import type { IAppState } from '~/appstate';
import type { IProductData } from '~/components/TagsModal/TagsModal';
import { ApiRequestError, ApiServerError, unwrapFetch2 } from '~/fetch';
import { default as route, default as Routes } from '~/helpers/Routes';
import * as Hub from '~/ogm-hub';
import {
	MetaProductType,
	MetaProductControllerV4,
	TagType,
	UmSelectionMode,
} from '~/ogm-hub';
import {
	getGroupingInformation,
	RearrangeSlice,
} from '~/Slices/Rearrange/RearrangeSlice';
import { selectSupplierById } from '~/Slices/SuppliersSlice';
import type { TagOption } from '~/Slices/TagsModalSlice';
import {
	buildTagSearchRequest,
	joinMetaProductNameSegments,
	tagLinkPoolForRearrangeDraft,
	tagLinksFromTagOptions,
} from '~/helpers/tagLinks';

/** Corp supplier code for product API calls when the suppliers slice is not loaded yet. */
function resolveDynamicRearrangeSupplierCode(state: IAppState): string | null {
	const blueprintPayload = state.metaProductListing.blueprint.payload;
	const supplierId = blueprintPayload?.supplierId;
	if (supplierId) {
		const supplier = selectSupplierById(state, supplierId);
		if (supplier?.dssiCode) {
			return supplier.dssiCode;
		}
	}

	const { sourceMetaProducts } = state.dynamicRearrange;
	for (const source of sourceMetaProducts) {
		for (const product of source.currentProducts) {
			const code = product.corpSupplierCode?.trim();
			if (code) {
				return code;
			}
		}
	}

	const lookup = state.rearrange.metaProducts.metaProductLookup;
	for (const key in lookup) {
		if (!Object.prototype.hasOwnProperty.call(lookup, key)) {
			continue;
		}
		const mp = lookup[key];
		for (const header of mp.productHeaders ?? []) {
			const code = header.corpSupplierCode?.trim();
			if (code) {
				return code;
			}
		}
	}

	return null;
}

function tagLinkPoolForDraftMetaProduct(
	state: IAppState,
	draftMetaProductId: number
): Hub.MetaProductTagLink[] {
	return tagLinkPoolForRearrangeDraft(
		state.dynamicRearrange.sourceMetaProducts.map(
			(source) => source.tagLinks ?? []
		),
		state.rearrange.metaProducts.metaProductLookup[draftMetaProductId]
			?.tagLinks ?? []
	);
}

export interface ISourceMetaProductTags {
	metaProductId: number;
	metaProductName: string;
	tagLinks: Hub.MetaProductTagLink[];
	selectedTags: TagOption[];
	currentProducts: IProductData[];
}

export interface IDraftGrouping {
	targetId: number;
	name: string;
	isNameDirty: boolean;
	selectedTags: SelectedTagSet;
	products: IProductData[];
	isLoadingProducts: boolean;
}

export interface SelectedTagSet {
	category: TagOption | null;
	primary: TagOption | null;
	packaging: TagOption | null;
	general: TagOption[];
}

export interface IDynamicRearrangeState {
	sourceMetaProducts: ISourceMetaProductTags[];

	allTags: Hub.Tag[];

	draftGroupings: IDraftGrouping[];

	isLoadingTags: boolean;
	isSaving: boolean;
	productTagsFetchFailCount: number;

	blueprintId: number | null;
	supplierCode: string | null;
	isDSSISite: boolean;
	isShowingDynamicRearrangeWarningModal: boolean;
}

const initialState: IDynamicRearrangeState = {
	sourceMetaProducts: [],
	allTags: [],
	draftGroupings: [
		{
			targetId: 0,
			name: '',
			isNameDirty: false,
			selectedTags: {
				category: null,
				primary: null,
				packaging: null,
				general: [],
			},
			products: [],
			isLoadingProducts: false,
		},
	],
	isLoadingTags: false,
	isSaving: false,
	productTagsFetchFailCount: 0,
	blueprintId: null,
	supplierCode: null,
	isDSSISite: false,
	isShowingDynamicRearrangeWarningModal: false,
};

export const tagToTagOption = (tag: Hub.Tag, excluded = false): TagOption => ({
	value: tag.id.toString(),
	label: tag.name,
	excluded,
});

/** Flat list of tag options in category → primary → general → packaging order. */
export const tagOptionsFromSelectedTagSet = (
	selectedTags: SelectedTagSet
): TagOption[] => {
	const options: TagOption[] = [];
	if (selectedTags.category) {
		options.push(selectedTags.category);
	}
	if (selectedTags.primary) {
		options.push(selectedTags.primary);
	}
	options.push(...selectedTags.general);
	if (selectedTags.packaging) {
		options.push(selectedTags.packaging);
	}
	return options;
};

export const generateNameFromSelectedTags = (
	selectedTags: SelectedTagSet
): string => {
	const cleanTagName = (label: string) => label.trim().replace(/\s+/g, ' ');

	const generalTags = [...selectedTags.general]
		.filter((tag) => !tag.excluded)
		.filter(
			(tag, index, arr) =>
				arr.findIndex(
					(t) => t.label.toLowerCase() === tag.label.toLowerCase()
				) === index
		)
		.sort((a, b) => {
			const aIsNumber = /^\d/.test(a.label);
			const bIsNumber = /^\d/.test(b.label);
			return (
				(aIsNumber ? 1 : 0) - (bIsNumber ? 1 : 0) ||
				a.label.localeCompare(b.label)
			);
		});

	const category =
		selectedTags.category && !selectedTags.category.excluded
			? cleanTagName(selectedTags.category.label)
			: undefined;
	const primary =
		selectedTags.primary && !selectedTags.primary.excluded
			? cleanTagName(selectedTags.primary.label)
			: undefined;
	const packaging =
		selectedTags.packaging && !selectedTags.packaging.excluded
			? cleanTagName(selectedTags.packaging.label)
			: undefined;
	const generalSegments = generalTags.map((tag) => cleanTagName(tag.label));

	return joinMetaProductNameSegments(
		category,
		primary,
		generalSegments,
		packaging
	).name;
};

export const fetchReferencedTags = createAsyncThunk(
	'dynamicRearrange/fetchReferencedTags',
	async (tagIds: number[], { rejectWithValue }) => {
		try {
			if (tagIds.length === 0) {
				return [] as Hub.Tag[];
			}

			const response = await Hub.TagController.GetTagsByIds({
				tagIds,
				includeEmbedding: false,
			});

			if (!response.ok) {
				return rejectWithValue('Failed to fetch tags from API');
			}

			return response.value.tags;
		} catch (error) {
			return rejectWithValue('Failed to fetch tags from API');
		}
	}
);

export const fetchProductsByTagsForDraft = createAsyncThunk(
	'dynamicRearrange/fetchProductsByTagsForDraft',
	async (
		params: {
			targetId: number;
			selectedTags: SelectedTagSet;
		},
		{ rejectWithValue, getState }
	) => {
		const state = getState() as IAppState;

		const blueprintPayload = state.metaProductListing.blueprint.payload;
		const blueprintId = blueprintPayload?.id;
		const isDSSISite = state.metaProductListing.siteTypeId === 2;
		const supplierCode = resolveDynamicRearrangeSupplierCode(state);

		if (!supplierCode || !blueprintId) {
			return rejectWithValue('Missing supplier code or blueprint ID');
		}

		const tagOptions = tagOptionsFromSelectedTagSet(params.selectedTags);
		const tagLinks = tagLinksFromTagOptions(
			tagOptions,
			state.dynamicRearrange.allTags,
			tagLinkPoolForDraftMetaProduct(state, params.targetId)
		);
		const request = buildTagSearchRequest(
			supplierCode,
			params.selectedTags,
			tagLinks
		);

		const eachesOnly =
			state.rearrange.umSelectionModeId === UmSelectionMode.EachOnly;

		try {
			const response = await Hub.DssiProductController.SearchProducts(
				request,
				blueprintId,
				isDSSISite,
				eachesOnly
			);

			if (!response.ok) {
				return rejectWithValue('Failed to fetch products by tags');
			}

			const responseValue = response.value;
			const productsWithTags = responseValue?.productsWithTags || [];

			const products: IProductData[] = productsWithTags.map((pwt) => ({
				id: pwt.product.id.toString(),
				productNumber: pwt.product.supplierProductNumber,
				supplierProductNumber: pwt.product.supplierProductNumber,
				corpSupplierCode: pwt.product.corpSupplierCode,
				productName: pwt.product.description,
				description: pwt.product.description,
				extendedDescription: pwt.product.extendedDescription,
				packSize: pwt.product.packSize,
				tags: pwt.tags,
				coveredDistributionCenters:
					pwt.product.coveredDistributionCenters?.map((dc) => ({
						distributionCenter: dc.distributionCenter,
						unitMeasureCodes: dc.unitMeasureCodes || [],
					})) || [],
				remoteStockDcs:
					pwt.product.remoteStockDcs?.map((dc) => ({
						distributionCenter: dc.distributionCenter,
						unitMeasureCodes: dc.unitMeasureCodes || [],
					})) || [],
				demandStockDcs:
					pwt.product.demandStockDcs?.map((dc) => ({
						distributionCenter: dc.distributionCenter,
						unitMeasureCodes: dc.unitMeasureCodes || [],
					})) || [],
				nonstockDcs:
					pwt.product.nonstockDcs?.map((dc) => ({
						distributionCenter: dc.distributionCenter,
						unitMeasureCodes: dc.unitMeasureCodes || [],
					})) || [],
			}));

			const { umSelectionModeId } = (getState() as IAppState).rearrange;
			const filteredProducts =
				umSelectionModeId === UmSelectionMode.EachOnly
					? products.filter((p) =>
							p.coveredDistributionCenters?.some((dc) =>
								dc.unitMeasureCodes?.some((um) => um.toLowerCase() === 'ea')
							)
					  )
					: products;

			return {
				targetId: params.targetId,
				products: filteredProducts,
			};
		} catch (error) {
			return rejectWithValue('Failed to fetch products by tags');
		}
	}
);

export const moveTagsAndFetchProducts = createAsyncThunk(
	'dynamicRearrange/moveTagsAndFetchProducts',
	async (
		params: {
			targetId: number;
			tags: TagOption[];
			allTags: Hub.Tag[];
		},
		{ dispatch, getState }
	) => {
		dispatch(dynamicRearrangeSlice.actions.moveTagsToGrouping(params));

		const state = getState() as IAppState;
		const grouping = state.dynamicRearrange.draftGroupings.find(
			(g) => g.targetId === params.targetId
		);

		if (grouping) {
			dispatch(
				fetchProductsByTagsForDraft({
					targetId: params.targetId,
					selectedTags: grouping.selectedTags,
				})
			);
		}

		return params.targetId;
	}
);

export const removeTagAndFetchProducts = createAsyncThunk(
	'dynamicRearrange/removeTagAndFetchProducts',
	async (
		params: {
			targetId: number;
			tagValue: string;
			tagType: TagType;
		},
		{ dispatch, getState }
	) => {
		dispatch(dynamicRearrangeSlice.actions.removeTagFromGrouping(params));

		const state = getState() as IAppState;
		const grouping = state.dynamicRearrange.draftGroupings.find(
			(g) => g.targetId === params.targetId
		);

		if (grouping) {
			const hasAnyTags =
				grouping.selectedTags.category !== null ||
				grouping.selectedTags.primary !== null ||
				grouping.selectedTags.packaging !== null ||
				grouping.selectedTags.general.length > 0;

			if (hasAnyTags) {
				dispatch(
					fetchProductsByTagsForDraft({
						targetId: params.targetId,
						selectedTags: grouping.selectedTags,
					})
				);
			} else {
				dispatch(
					dynamicRearrangeSlice.actions.setGroupingProducts({
						targetId: params.targetId,
						products: [],
					})
				);
			}
		}

		return params.targetId;
	}
);

export const fetchProductTagsForSourceMetaProducts = createAsyncThunk(
	'dynamicRearrange/fetchProductTagsForSourceMetaProducts',
	async (_, { getState, rejectWithValue }) => {
		const state = getState() as IAppState;
		const { sourceMetaProducts } = state.dynamicRearrange;

		const blueprintPayload = state.metaProductListing.blueprint.payload;
		const blueprintId = blueprintPayload?.id;
		const isDSSISite = state.metaProductListing.siteTypeId === 2;
		const supplierCode = resolveDynamicRearrangeSupplierCode(state);

		if (!supplierCode || !blueprintId || sourceMetaProducts.length === 0) {
			return rejectWithValue(
				'No supplier code, blueprint ID, or source meta products'
			);
		}

		const allProductNumbers: string[] = [];
		sourceMetaProducts.forEach((smp) => {
			smp.currentProducts.forEach((p) => {
				if (
					p.supplierProductNumber &&
					!allProductNumbers.includes(p.supplierProductNumber)
				) {
					allProductNumbers.push(p.supplierProductNumber);
				}
			});
		});

		if (allProductNumbers.length === 0) {
			return rejectWithValue('No products to fetch tags for');
		}

		const eachesOnly =
			(getState() as IAppState).rearrange.umSelectionModeId ===
			UmSelectionMode.EachOnly;

		try {
			const response =
				await Hub.DssiProductController.GetProductsWithTagsByProductNumbers(
					{
						corpSupplierCode: supplierCode,
						supplierProductNumbers: allProductNumbers,
					},
					blueprintId,
					isDSSISite,
					eachesOnly
				);

			if (!response.ok || !response.value) {
				return rejectWithValue('Failed to fetch product tags');
			}

			const productTagsMap: Record<string, Hub.Tag[]> = {};
			response.value.products.forEach((pwt) => {
				if (pwt.product?.supplierProductNumber) {
					productTagsMap[pwt.product.supplierProductNumber] = pwt.tags || [];
				}
			});

			return productTagsMap;
		} catch (error) {
			return rejectWithValue('Failed to fetch product tags');
		}
	}
);

export const saveDynamicGroupings = createAsyncThunk<
	{ remainingGroupingIdsToReview: number[] },
	{ blueprintId: number },
	{ state: IAppState }
>(
	'dynamicRearrange/saveDynamicGroupings',
	async (args, { rejectWithValue, dispatch, getState }) => {
		try {
			const state = getState();
			const { draftGroupings, sourceMetaProducts } = state.dynamicRearrange;
			const existingGroupings = state.rearrange.groupings.groupings;

			const draftsWithProducts = draftGroupings.filter(
				(d) => d.products.length > 0
			);
			const isSingleDestination = draftsWithProducts.length === 1;

			const requests: Hub.UpsertMetaProductGroupingRequest[] = draftGroupings
				.map((draft) => {
					const hasValidProducts = draft.products.some((p) => {
						const smp = sourceMetaProducts.find((s) =>
							s.currentProducts.some(
								(cp) => cp.supplierProductNumber === p.supplierProductNumber
							)
						);
						return smp;
					});

					if (!hasValidProducts) {
						return null;
					}

					const { selectedTags } = draft;
					const tagLinks = tagLinksFromTagOptions(
						tagOptionsFromSelectedTagSet(selectedTags),
						state.dynamicRearrange.allTags,
						tagLinkPoolForDraftMetaProduct(state, draft.targetId)
					);

					const existingGrouping =
						draft.targetId > 0
							? existingGroupings.find(
									(g) => g.metaProductId === draft.targetId
							  )
							: undefined;
					const metaProductGroupingId = existingGrouping?.id ?? null;

					const draftRemoves: Hub.MetaProductGroupingRemove[] = [];
					const productsBySource: Record<number, Set<string>> = {};

					if (isSingleDestination) {
						sourceMetaProducts.forEach((smp) => {
							if (!productsBySource[smp.metaProductId]) {
								productsBySource[smp.metaProductId] = new Set();
							}
							smp.currentProducts.forEach((cp) => {
								productsBySource[smp.metaProductId].add(
									cp.supplierProductNumber
								);
							});
						});
					} else {
						draft.products.forEach((p) => {
							const smp = sourceMetaProducts.find((s) =>
								s.currentProducts.some(
									(cp) => cp.supplierProductNumber === p.supplierProductNumber
								)
							);
							if (smp) {
								if (!productsBySource[smp.metaProductId]) {
									productsBySource[smp.metaProductId] = new Set();
								}
								productsBySource[smp.metaProductId].add(
									p.supplierProductNumber
								);
							}
						});
					}

					for (const [sourceId, products] of Object.entries(productsBySource)) {
						draftRemoves.push({
							sourceMetaProductId: Number(sourceId),
							productNumbers: Array.from(products),
						});
					}

					const additionalProductNumbers = draft.products.map(
						(p) => p.supplierProductNumber
					);

					return {
						metaProductGroupingId,
						metaProductGroupingRemoves: draftRemoves,
						newMetaProductName: draft.name || 'Dynamic Group',
						metaProductType: MetaProductType.Dynamic,
						tagLinks,
						additionalProductNumbers,
					} as Hub.UpsertMetaProductGroupingRequest;
				})
				.filter((x): x is Hub.UpsertMetaProductGroupingRequest => x !== null);

			const result = await unwrapFetch2(
				MetaProductControllerV4.UpsertMetaProductGroupings({
					blueprintId: args.blueprintId,
					source: state.rearrange.peerGroupingSource,
					isSelectByUm: state.rearrange.isSelectByUm,
					umSelectionModeId: state.rearrange.umSelectionModeId,
					unitMeasureCodes:
						state.rearrange.umSelectionModeId === UmSelectionMode.EachOnly
							? ['EA']
							: [],
					requests,
				})
			);

			const groupingIdsInSet: number[] = uniq(
				existingGroupings
					.filter((x) => x.id != null)
					.map((x) => x.id)
					.concat(result.responses.map((x) => x.groupingId))
			);

			const remainingGroupingIdsToReview: number[] = uniq(
				result.responses.map((x) => x.groupingId)
			);
			const firstMetaProductIdToReview =
				result.responses[0].groupedMetaProductId;

			dispatch(
				RearrangeSlice.actions.setRemainingGroupingIdsToReview(
					remainingGroupingIdsToReview
				)
			);

			dispatch(
				replace({
					pathname: Routes.rearrange(args.blueprintId),
					query: { gid: groupingIdsInSet },
				})
			);
			dispatch(
				push(
					route.prioritize(
						args.blueprintId.toString(),
						firstMetaProductIdToReview,
						true
					)
				)
			);

			return {
				remainingGroupingIdsToReview,
			};
		} catch (err) {
			toastr.error('Saving Failed', 'Saving failed.  Please try again.');

			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const dynamicRearrangeSlice = createSlice({
	name: 'dynamicRearrange',
	initialState,
	reducers: {
		initialize: (
			state,
			action: PayloadAction<{
				blueprintId: number;
				supplierCode: string;
				isDSSISite: boolean;
			}>
		) => {
			state.blueprintId = action.payload.blueprintId;
			state.supplierCode = action.payload.supplierCode;
			state.isDSSISite = action.payload.isDSSISite;
		},

		setSourceMetaProducts: (
			state,
			action: PayloadAction<ISourceMetaProductTags[]>
		) => {
			state.sourceMetaProducts = action.payload;
		},

		toggleTagSelection: (
			state,
			action: PayloadAction<{
				metaProductId: number;
				tag: TagOption;
				allTags: Hub.Tag[];
			}>
		) => {
			const { metaProductId, tag, allTags } = action.payload;
			const sourceMetaProduct = state.sourceMetaProducts.find(
				(mp) => mp.metaProductId === metaProductId
			);

			if (!sourceMetaProduct) {
				return;
			}

			const tagIndex = sourceMetaProduct.selectedTags.findIndex(
				(t) => t.value === tag.value
			);

			if (tagIndex > -1) {
				sourceMetaProduct.selectedTags.splice(tagIndex, 1);
			} else {
				const tagId = parseInt(tag.value);
				const fullTag = allTags.find((t) => t.id === tagId);
				const tagType = fullTag?.tagType;

				if (
					tagType === TagType.Category ||
					tagType === TagType.Primary ||
					tagType === TagType.Packaging
				) {
					state.sourceMetaProducts.forEach((mp) => {
						mp.selectedTags = mp.selectedTags.filter((existingTag) => {
							const existingTagId = parseInt(existingTag.value);
							const existingFullTag = allTags.find(
								(t) => t.id === existingTagId
							);
							return existingFullTag?.tagType !== tagType;
						});
					});
				}

				sourceMetaProduct.selectedTags.push({ ...tag });
			}
		},

		toggleTagGroupSelection: (
			state,
			action: PayloadAction<{
				metaProductId: number;
				tags: TagOption[];
				allTags: Hub.Tag[];
			}>
		) => {
			const { metaProductId, tags, allTags } = action.payload;
			if (tags.length === 0) {
				return;
			}

			const sourceMetaProduct = state.sourceMetaProducts.find(
				(mp) => mp.metaProductId === metaProductId
			);
			if (!sourceMetaProduct) {
				return;
			}

			const memberValues = new Set(tags.map((tag) => tag.value));
			const allSelected = tags.every((tag) =>
				sourceMetaProduct.selectedTags.some(
					(selected) => selected.value === tag.value
				)
			);

			if (allSelected) {
				sourceMetaProduct.selectedTags = sourceMetaProduct.selectedTags.filter(
					(selected) => !memberValues.has(selected.value)
				);
				return;
			}

			const firstTagId = parseInt(tags[0].value, 10);
			const tagType = allTags.find((t) => t.id === firstTagId)?.tagType;
			if (
				tagType === TagType.Category ||
				tagType === TagType.Primary ||
				tagType === TagType.Packaging
			) {
				state.sourceMetaProducts.forEach((mp) => {
					mp.selectedTags = mp.selectedTags.filter((existingTag) => {
						const existingTagId = parseInt(existingTag.value, 10);
						const existingFullTag = allTags.find(
							(t) => t.id === existingTagId
						);
						return existingFullTag?.tagType !== tagType;
					});
				});
			}

			tags.forEach((tag) => {
				if (
					!sourceMetaProduct.selectedTags.some(
						(selected) => selected.value === tag.value
					)
				) {
					sourceMetaProduct.selectedTags.push({ ...tag });
				}
			});
		},

		clearTagSelections: (state, action: PayloadAction<number>) => {
			const sourceMetaProduct = state.sourceMetaProducts.find(
				(mp) => mp.metaProductId === action.payload
			);
			if (sourceMetaProduct) {
				sourceMetaProduct.selectedTags = [];
			}
		},

		addDraftGrouping: (state) => {
			const newTargetId =
				state.draftGroupings.length > 0
					? Math.max(...state.draftGroupings.map((g) => g.targetId)) + 1
					: 0;
			state.draftGroupings.push({
				targetId: newTargetId,
				name: '',
				isNameDirty: false,
				selectedTags: {
					category: null,
					primary: null,
					packaging: null,
					general: [],
				},
				products: [],
				isLoadingProducts: false,
			});
		},

		moveTagsToGrouping: (
			state,
			action: PayloadAction<{
				targetId: number;
				tags: TagOption[];
				allTags: Hub.Tag[];
			}>
		) => {
			const { targetId, tags, allTags } = action.payload;
			const grouping = state.draftGroupings.find(
				(g) => g.targetId === targetId
			);

			if (!grouping) {
				return;
			}

			tags.forEach((tagOption) => {
				const tagId = parseInt(tagOption.value);
				const fullTag = allTags.find((t) => t.id === tagId);

				if (!fullTag) {
					return;
				}

				switch (fullTag.tagType) {
					case TagType.Category:
						grouping.selectedTags.category = tagOption;
						break;
					case TagType.Primary:
						grouping.selectedTags.primary = tagOption;
						break;
					case TagType.Packaging:
						grouping.selectedTags.packaging = tagOption;
						break;
					case TagType.General: {
						const exists = grouping.selectedTags.general.some(
							(g) => g.value === tagOption.value
						);
						if (!exists) {
							grouping.selectedTags.general.push(tagOption);
						}
						break;
					}
				}
			});

			if (!grouping.isNameDirty) {
				grouping.name = generateNameFromSelectedTags(grouping.selectedTags);
			}

			state.sourceMetaProducts.forEach((mp) => {
				mp.selectedTags = [];
			});
		},

		removeTagFromGrouping: (
			state,
			action: PayloadAction<{
				targetId: number;
				tagValue: string;
				tagType: TagType;
			}>
		) => {
			const { targetId, tagValue, tagType } = action.payload;
			const grouping = state.draftGroupings.find(
				(g) => g.targetId === targetId
			);

			if (!grouping) {
				return;
			}

			switch (tagType) {
				case TagType.Category:
					if (grouping.selectedTags.category?.value === tagValue) {
						grouping.selectedTags.category = null;
					}
					break;
				case TagType.Primary:
					if (grouping.selectedTags.primary?.value === tagValue) {
						grouping.selectedTags.primary = null;
					}
					break;
				case TagType.Packaging:
					if (grouping.selectedTags.packaging?.value === tagValue) {
						grouping.selectedTags.packaging = null;
					}
					break;
				case TagType.General:
					grouping.selectedTags.general = grouping.selectedTags.general.filter(
						(g) => g.value !== tagValue
					);
					break;
			}

			if (!grouping.isNameDirty) {
				grouping.name = generateNameFromSelectedTags(grouping.selectedTags);
			}
		},

		clearDraftGroupingTags: (state, action: PayloadAction<number>) => {
			const targetId = action.payload;
			const grouping = state.draftGroupings.find(
				(g) => g.targetId === targetId
			);

			if (!grouping) {
				return;
			}

			grouping.selectedTags = {
				category: null,
				primary: null,
				packaging: null,
				general: [],
			};
			grouping.products = [];

			if (!grouping.isNameDirty) {
				grouping.name = '';
			}
		},

		renameDraftGrouping: (
			state,
			action: PayloadAction<{
				targetId: number;
				name: string;
			}>
		) => {
			const grouping = state.draftGroupings.find(
				(g) => g.targetId === action.payload.targetId
			);
			if (grouping) {
				grouping.name = action.payload.name;
				grouping.isNameDirty = true;
			}
		},

		resetDraftGroupingName: (state, action: PayloadAction<number>) => {
			const grouping = state.draftGroupings.find(
				(g) => g.targetId === action.payload
			);
			if (grouping) {
				grouping.isNameDirty = false;
				grouping.name = generateNameFromSelectedTags(grouping.selectedTags);
			}
		},

		setGroupingProducts: (
			state,
			action: PayloadAction<{
				targetId: number;
				products: IProductData[];
			}>
		) => {
			const grouping = state.draftGroupings.find(
				(g) => g.targetId === action.payload.targetId
			);
			if (grouping) {
				grouping.products = action.payload.products;
			}
		},

		removeDraftGrouping: (state, action: PayloadAction<number>) => {
			state.draftGroupings = state.draftGroupings.filter(
				(g) => g.targetId !== action.payload
			);

			if (state.draftGroupings.length === 0) {
				state.draftGroupings.push({
					targetId: 0,
					name: '',
					isNameDirty: false,
					selectedTags: {
						category: null,
						primary: null,
						packaging: null,
						general: [],
					},
					products: [],
					isLoadingProducts: false,
				});
			}
		},

		syncDraftsFromServerGroupings: (
			state,
			action: PayloadAction<
				{
					targetId: number;
					name: string;
					selectedTags: SelectedTagSet;
				}[]
			>
		) => {
			const serverDrafts = action.payload;
			if (serverDrafts.length === 0) {
				state.draftGroupings = initialState.draftGroupings;
				return;
			}

			state.draftGroupings = serverDrafts.map((draft) => ({
				targetId: draft.targetId,
				name: draft.name,
				isNameDirty: false,
				selectedTags: draft.selectedTags,
				products: [],
				isLoadingProducts: true,
			}));
		},
		backfillDraftTags: (
			state,
			action: PayloadAction<
				{ targetId: number; selectedTags: SelectedTagSet }[]
			>
		) => {
			for (const update of action.payload) {
				const draft = state.draftGroupings.find(
					(d) => d.targetId === update.targetId
				);
				if (draft) {
					draft.selectedTags = update.selectedTags;
				}
			}
		},

		showDynamicRearrangeWarningModal: (state) => {
			state.isShowingDynamicRearrangeWarningModal = true;
		},

		hideDynamicRearrangeWarningModal: (state) => {
			state.isShowingDynamicRearrangeWarningModal = false;
		},

		reset: () => initialState,
	},
	extraReducers: (builder) => {
		builder
			.addCase(saveDynamicGroupings.pending, (state) => {
				state.isSaving = true;
			})
			.addCase(fetchReferencedTags.pending, (state) => {
				state.isLoadingTags = true;
			})
			.addCase(fetchReferencedTags.fulfilled, (state, action) => {
				state.isLoadingTags = false;
				state.allTags = action.payload;
			})
			.addCase(fetchReferencedTags.rejected, (state) => {
				state.isLoadingTags = false;
			})
			.addCase(fetchProductsByTagsForDraft.pending, (state, action) => {
				const grouping = state.draftGroupings.find(
					(g) => g.targetId === action.meta.arg.targetId
				);
				if (grouping) {
					grouping.isLoadingProducts = true;
				}
			})
			.addCase(fetchProductsByTagsForDraft.fulfilled, (state, action) => {
				const grouping = state.draftGroupings.find(
					(g) => g.targetId === action.payload.targetId
				);
				if (grouping) {
					grouping.isLoadingProducts = false;
					grouping.products = action.payload.products;
				}
			})
			.addCase(fetchProductsByTagsForDraft.rejected, (state, action) => {
				const targetId = action.meta.arg.targetId;
				const grouping = state.draftGroupings.find(
					(g) => g.targetId === targetId
				);
				if (grouping) {
					grouping.isLoadingProducts = false;
				}
			})
			.addCase(
				fetchProductTagsForSourceMetaProducts.fulfilled,
				(state, action) => {
					const productTagsMap = action.payload;
					state.productTagsFetchFailCount = 0;
					state.sourceMetaProducts.forEach((smp) => {
						smp.currentProducts = smp.currentProducts.map((product) => ({
							...product,
							tags: productTagsMap[product.supplierProductNumber] || [],
						}));
					});

					const existingTagIds = new Set(state.allTags.map((t) => t.id));
					Object.values(productTagsMap).forEach((tags) => {
						tags.forEach((tag) => {
							if (!existingTagIds.has(tag.id)) {
								state.allTags.push(tag);
								existingTagIds.add(tag.id);
							}
						});
					});
				}
			)
			.addCase(fetchProductTagsForSourceMetaProducts.rejected, (state) => {
				state.productTagsFetchFailCount += 1;
			})
			.addCase(saveDynamicGroupings.fulfilled, (state) => {
				state.isSaving = false;
				state.draftGroupings = initialState.draftGroupings;
			})
			.addCase(saveDynamicGroupings.rejected, (state) => {
				state.isSaving = false;
			})
			.addCase(getGroupingInformation.pending, (state) => {
				state.sourceMetaProducts = initialState.sourceMetaProducts;
				state.allTags = initialState.allTags;
				state.productTagsFetchFailCount = 0;
			})
			.addCase(getGroupingInformation.fulfilled, (state, action) => {
				const { existingGroupings, metaProducts } = action.payload;

				const mpLookup: Record<number, Hub.GetMetaProductResponse> = {};
				for (const mp of metaProducts) {
					mpLookup[mp.id] = mp;
				}

				const newGroupings = existingGroupings.filter(
					(g) =>
						g.metaProductId != null &&
						g.metaProductId > 0 &&
						g.status === Hub.MetaProductGroupingStatus.New
				);

				if (newGroupings.length === 0) {
					state.draftGroupings = initialState.draftGroupings;
					return;
				}

				const allTags = state.allTags;

				state.draftGroupings = newGroupings.map((g) => {
					const mp = mpLookup[g.metaProductId!];

					const products: IProductData[] = mp
						? (mp as any).productHeaders?.map((ph: any, idx: number) => ({
								id: `${mp.id}-${ph.supplierProductNumber}-${idx}`,
								productNumber: ph.supplierProductNumber,
								supplierProductNumber: ph.supplierProductNumber,
								corpSupplierCode: ph.corpSupplierCode ?? '',
								productName: ph.description ?? '',
								description: ph.description ?? '',
								extendedDescription: ph.extendedDescription ?? '',
								packSize: ph.packSize ?? '',
								coveredDistributionCenters: ph.coveredDistributionCenters,
								remoteStockDcs: ph.remoteStockDcs,
								demandStockDcs: ph.demandStockDcs,
								nonstockDcs: ph.nonstockDcs,
						  })) ?? []
						: [];

					const selectedTags: SelectedTagSet = {
						category: null,
						primary: null,
						packaging: null,
						general: [],
					};

					if (mp && allTags.length > 0) {
						for (const tagLink of mp.tagLinks ?? []) {
							const tag = allTags.find((t) => t.id === tagLink.tagId);
							if (!tag) {
								continue;
							}
							const opt = tagToTagOption(tag, tagLink.excluded);
							switch (tag.tagType) {
								case TagType.Category:
									selectedTags.category = opt;
									break;
								case TagType.Primary:
									selectedTags.primary = opt;
									break;
								case TagType.Packaging:
									selectedTags.packaging = opt;
									break;
								case TagType.General:
									selectedTags.general.push(opt);
									break;
							}
						}
					}

					return {
						targetId: g.metaProductId!,
						name: mp?.name ?? g.name,
						isNameDirty: false,
						selectedTags,
						products,
						isLoadingProducts: false,
					};
				});
			});
	},
});

export const DynamicRearrangeSlice = dynamicRearrangeSlice;
export default dynamicRearrangeSlice.reducer;
